import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SelectSourceMetadataFieldForCohortGroup } from '@modules/integrationsV2/components/CreateOrUpdateCohortGroupModalContent/SelectSourceMetadataFieldForCohortGroup';
import { SelectUnifiedMetadataFieldForCohortGroup } from '@modules/integrationsV2/components/CreateOrUpdateCohortGroupModalContent/SelectUnifiedMetadataFieldForCohortGroup';
import {
  useFetchConnectedIntegrations,
  useUpdateIntegration,
} from '@modules/integrationsV2/hooks';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
  IntegrationViewFragment,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { useEffect, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const trackCohortGroupEvents = createAnalyticsTracker('cohortGroup');

interface CreateOrUpdateCohortGroupModalContentProps {
  integrationID?: string;
}

export const CreateOrUpdateCohortGroupModalContent = ({
  integrationID,
}: CreateOrUpdateCohortGroupModalContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [customGroupName, setCustomGroupName] = useState('');
  const [selectedMetadataField, setSelectedMetadataField] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isUpdateAction = !!integrationID;

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const currentSelectedMetadataField =
    integrationID &&
    connectedIntegrations?.find(
      (integration) => integration.id === integrationID,
    )?.data?.feedbackMetadataEnrichment?.metadataKeys?.[0];

  useEffect(() => {
    if (currentSelectedMetadataField) {
      setSelectedMetadataField(currentSelectedMetadataField);
    }
  }, [currentSelectedMetadataField]);

  const { status: createIntegrationStatus, createIntegration } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.FileUpload,
      integrationType: IntegrationType.Cohorts,
      createdByUser: userID,
      inputData: {
        feedbackMetadataEnrichment: {
          metadataEnrichmentName: customGroupName,
          metadataKeys: [selectedMetadataField],
        },
      },
      onError: (error: Error) => {
        if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
          setErrorMessage(IntegrationNameErrorMessages.DUPLICATE_NAME);
          return;
        }
        showErrorNotification({
          contentComponent: `Something went wrong${
            error.message ? `: ${error.message}` : '!'
          }`,
          props: {},
        });
      },
      onCompleted: () => {
        closeModal();
        trackCohortGroupEvents({
          eventName: 'Cohort Group - Integration Created',
          cohortGroupName: customGroupName,
          selectedMetadataField,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.FileUpload,
      integrationType: IntegrationType.Cohorts,
      onCompleted: () => {
        closeModal();
        trackCohortGroupEvents({
          eventName: 'Cohort Group - Integration Updated',
          cohortGroupName: customGroupName,
          selectedMetadataField,
        });
      },
    });

  const integrationsToDisplayGroupedBySource = connectedIntegrations
    .filter(
      (integration) =>
        integration.integrationType === IntegrationType.Feedback ||
        integration.integrationType === IntegrationType.User ||
        integration.integrationType === IntegrationType.Account,
    )
    .reduce((acc: Record<string, IntegrationViewFragment>, integration) => {
      if (integration.source && !acc[integration.source]) {
        acc[integration.source] = integration;
      }
      return acc;
    }, {} as Record<string, IntegrationViewFragment>);

  const integrationsToDisplay = Object.values(
    integrationsToDisplayGroupedBySource,
  ).sort((a, b) => a.channel.displayName.localeCompare(b.channel.displayName));

  const isSubmitRequestInProgress =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  const onSubmit = () => {
    if (isUpdateAction) {
      updateIntegration({
        integrationID,
        isActive: true,
        updateData: {
          feedbackMetadataEnrichment: {
            metadataKeys: [selectedMetadataField],
          },
        },
      });
    } else {
      createIntegration();
    }
  };

  const isSubmitButtonDisabled = isUpdateAction
    ? !selectedMetadataField
    : !customGroupName || !selectedMetadataField;

  const description = isUpdateAction
    ? `Update your cohort for any of your metadata fields, such as User ID, Opp ID, etc. Modify the values as needed, and Enterpret will match and regroup the feedback records accordingly.`
    : `Create a cohort for any of your metadata fields, such as User ID, Opp ID, etc. Upload a CSV containing the values, and Enterpret will match & group the feedback records accordingly.`;

  return (
    <>
      <ModalHeader
        title={`${isUpdateAction ? 'Update' : 'Create'} Cohort`}
        onClose={() => closeModal()}
      />
      <Divider />
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">{description}</Typography>
          {!isUpdateAction && (
            <TextField
              fullWidth
              autoFocus
              label="Cohort Name"
              value={customGroupName}
              onChange={(e) => setCustomGroupName(e.target.value)}
              placeholder="Enter a descriptive name for your cohort"
              required
              error={Boolean(errorMessage)}
              helperText={errorMessage}
            />
          )}
          <Stack spacing={2}>
            <Typography variant="body2">
              Select the metadata field (e.g., User ID, Opp ID) that will be
              used to create your group.
            </Typography>
            <Stack mt={2} height={300} sx={{ overflowY: 'auto' }}>
              {integrationsToDisplay.length > 0 && (
                <Box
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <SelectUnifiedMetadataFieldForCohortGroup
                    value={selectedMetadataField}
                    onChange={(value) => setSelectedMetadataField(value)}
                  />
                </Box>
              )}
              {integrationsToDisplay.length > 0 ? (
                integrationsToDisplay.map((integration) => {
                  return (
                    <Box
                      key={integration.source}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <SelectSourceMetadataFieldForCohortGroup
                        integration={integration}
                        value={selectedMetadataField}
                        onChange={(value) => setSelectedMetadataField(value)}
                      />
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body1" textAlign="center" mt={4}>
                  No integration sources found.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button
          variant="outlined"
          onClick={() => closeModal()}
          disabled={isSubmitRequestInProgress}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          autoFocus
          onClick={onSubmit}
          loading={isSubmitRequestInProgress}
          disabled={isSubmitButtonDisabled}
        >
          {isUpdateAction ? 'Update' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </>
  );
};
